import React, { useEffect, useRef, useState } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { useWebSocket } from "../../context/WebSocketContext";
import { formatTime } from "../../utils/format";
import "./NotesBoard.css";

const GAMES_CLOUD_BASE = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
    .replace(/^ws/, "http")
    .replace(/ws$/, "");

// firstName keeps the folded one-liner short: "Michelle" rather than "Michelle Lund Jensen".
const firstName = name => (name || "").trim().split(/\s+/)[0] || "";

// chime plays a short two-tone ping for a note from another GM. Synthesised so there is no
// audio asset; silently does nothing if the browser refuses (no user gesture yet, no audio).
function chime() {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const t = ctx.currentTime;
        osc.type = "sine";
        osc.frequency.setValueAtTime(880, t);
        osc.frequency.setValueAtTime(1175, t + 0.12);
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.45);
        osc.onended = () => ctx.close();
    } catch {
        // audio is a nicety, never an error
    }
}

// NotesBoard is the shared GM notes board for the room: short remarks about the running game
// that every GM on the room sees the moment they are posted. The board is the current
// "session" (the period between two timer resets); a reset wipes it and games-cloud attaches the
// notes to the game's "afslut spil" row. State lives in the WebSocketContext (`notes`), fed by the
// "notes" broadcast; this component fetches the board on mount/reconnect and posts new notes.
//
// Because GMs use it to reach each other, a note from someone else must be hard to miss: the
// board unfolds, chimes, pulses its frame, badges the count, highlights the note and prefixes the
// tab title — all until the GM acknowledges by clicking the board or focusing the input.
export default function NotesBoard() {
    const { notes, setNotes, gameDetailConfig, connectionStatus } = useWebSocket();
    const gameId = gameDetailConfig?.game_id;
    const ownEmail = (localStorage.getItem("userEmail") || "").toLowerCase();

    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [loaded, setLoaded] = useState(false);
    // IDs of notes from other GMs that this GM has not acknowledged yet.
    const [unseen, setUnseen] = useState(() => new Set());
    const listRef = useRef(null);
    const inputRef = useRef(null);
    // The highest note id we have already reacted to — anything above it is a new arrival. Set
    // by the first load so the notes that were already on the board don't count as new.
    const maxKnownRef = useRef(0);
    const baseTitleRef = useRef(document.title);

    const latest = notes.length > 0 ? notes[notes.length - 1] : null;
    const hasUnseen = unseen.size > 0;

    // Load the board when the room opens and again after every reconnect, so a broadcast missed
    // while the socket was down never leaves this HUD behind.
    useEffect(() => {
        if (!gameId) return;
        let cancelled = false;
        fetchWithAuth(`${GAMES_CLOUD_BASE}game/${encodeURIComponent(gameId)}/notes`)
            .then(r => r.json())
            .then(data => {
                if (cancelled) return;
                const list = data.notes || [];
                setLoaded(prev => {
                    if (!prev) maxKnownRef.current = Math.max(0, ...list.map(n => n.id));
                    return true;
                });
                setNotes(list);
                setError(null);
            })
            .catch(err => { if (!cancelled) setError(/503/.test(err.message) ? "Noter er ikke slået til" : "Kunne ikke hente noter"); });
        return () => { cancelled = true; };
    }, [gameId, connectionStatus, setNotes]);

    // React to new arrivals: notes above the high-water mark that someone else wrote.
    useEffect(() => {
        if (!loaded) return;
        if (notes.length === 0) {
            // Board wiped (timer reset / report) — nothing left to acknowledge.
            setUnseen(prev => (prev.size ? new Set() : prev));
            return;
        }
        const fresh = notes.filter(n => n.id > maxKnownRef.current);
        maxKnownRef.current = Math.max(maxKnownRef.current, ...notes.map(n => n.id));
        const fromOthers = fresh.filter(n => n.author_email !== ownEmail);
        if (fromOthers.length === 0) return;
        setUnseen(prev => {
            const next = new Set(prev);
            fromOthers.forEach(n => next.add(n.id));
            return next;
        });
        setCollapsed(false);
        chime();
    }, [notes, loaded, ownEmail]);

    // Keep the newest note in view.
    useEffect(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [notes, collapsed]);

    // Unread count in the tab title, so a GM on another tab still sees it.
    useEffect(() => {
        const base = baseTitleRef.current;
        document.title = hasUnseen ? `(${unseen.size}) ${base}` : base;
        return () => { document.title = base; };
    }, [hasUnseen, unseen.size]);

    const acknowledge = () => {
        if (hasUnseen) setUnseen(new Set());
    };

    const send = async () => {
        const body = text.trim();
        if (!body || sending || !gameId) return;
        setSending(true);
        setError(null);
        try {
            const res = await fetchWithAuth(`${GAMES_CLOUD_BASE}game/${encodeURIComponent(gameId)}/notes`, {
                method: "POST",
                body: JSON.stringify({ text: body }),
            });
            const note = await res.json();
            // The broadcast normally lands first; add it ourselves only if it has not.
            setNotes(prev => (prev.some(n => n.id === note.id) ? prev : [...prev, note]));
            setText("");
            inputRef.current?.focus();
        } catch (err) {
            setError("Noten blev ikke gemt");
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    return (
        <div className={`notes-board ${hasUnseen ? "has-unseen" : ""}`} onClick={acknowledge}>
            <div
                className="notes-board-header"
                onClick={() => setCollapsed(c => !c)}
                role="button"
                aria-expanded={!collapsed}
                title={collapsed ? "Vis noter" : "Skjul noter"}
            >
                <h3 className="notes-board-title">
                    Noter
                    {notes.length > 0 && <span className="notes-board-count">{notes.length}</span>}
                    {hasUnseen && <span className="notes-board-new">{unseen.size} ny{unseen.size > 1 ? "e" : ""}</span>}
                </h3>
                {collapsed && latest && (
                    <div className="notes-board-latest" title={latest.text}>
                        <span className="notes-board-time">{formatTime(latest.created_at)}</span>
                        <span className="notes-board-author">{firstName(latest.author_name)}</span>
                        <span className="notes-board-latest-text">{latest.text}</span>
                    </div>
                )}
                <button
                    type="button"
                    className="notes-board-toggle"
                    tabIndex={-1}
                    aria-hidden="true"
                >
                    <img src={process.env.PUBLIC_URL + (collapsed ? "/plus.svg" : "/minus.svg")} alt="" />
                </button>
            </div>
            {!collapsed && (
                <>
                    <div className="notes-board-list" ref={listRef}>
                        {notes.length === 0 && !error && (
                            <div className="notes-board-empty">Ingen noter endnu. Boardet ryddes, når timeren resettes.</div>
                        )}
                        {notes.map(n => (
                            <div
                                key={n.id}
                                className={`notes-board-note ${n.author_email === ownEmail ? "own" : ""} ${unseen.has(n.id) ? "unseen" : ""}`}
                            >
                                <span className="notes-board-meta">
                                    <span className="notes-board-time">{formatTime(n.created_at)}</span>
                                    <span className="notes-board-author">{n.author_name}</span>
                                </span>
                                <span className="notes-board-text">{n.text}</span>
                            </div>
                        ))}
                    </div>
                    {error && <div className="notes-board-error">{error}</div>}
                    <div className="notes-board-input">
                        <textarea
                            ref={inputRef}
                            rows={1}
                            value={text}
                            placeholder="Skriv en note til de andre GM'er…"
                            disabled={sending || !gameId}
                            onChange={e => setText(e.target.value)}
                            onKeyDown={onKeyDown}
                            onFocus={acknowledge}
                        />
                        <button type="button" className="notes-board-send" onClick={send} disabled={sending || !text.trim() || !gameId}>
                            Post
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
